import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { GuardedButton } from '../../components/GuardedButton';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { LieferungAddonCard } from '../../components/LieferungAddonCard';
import { 
  MapPin, 
  Users, 
  Package, 
  Settings,
  Loader2,
  Plus,
  ChevronRight,
  Edit2,
  Trash2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useSubscriptionStatus } from '../../hooks/useSubscriptionStatus';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const BRAND_OPTIONS = [
  'JET','Shell','Aral','Esso','TotalEnergies','OMV','AVIA',
  'Agip / Eni','HEM','Star','Orlen','Raiffeisen',
  'RAN (Südramol)','Freie Tankstelle / Sonstige'
];

// Helper function to get German module names
const getModuleName = (moduleId) => {
  const moduleNames = {
    'backshop': 'Backshop',
    'lotto': 'Lotto',
    'mhd_control': 'MHD-Verwaltung',
    'temperature_control': 'Temperaturkontrolle',
    'tasks': 'Aufgaben',
    'unterweisungen': 'Unterweisungen',
    'employee_management': 'Mitarbeiterverwaltung',
    'general_writeoffs': 'Allgemeine Abschriften',
    'instructions': 'Schulungen',
    'orders': 'Bestellungen',
    'mhd': 'MHD',
    'temperature': 'Temperatur'
  };
  
  return moduleNames[moduleId] || moduleId;
};

export const AdminLocations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [needsPayment, setNeedsPayment] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: '', address: '', station_number: '', brand: '', task_daily_summary_time: '23:45' });
  const [employeeLimits, setEmployeeLimits] = useState({ total_employees: 0, location_count: 0, extra_employees: 0, cost_per_month: 0 });
  const [costPreview, setCostPreview] = useState(null);
  const navigate = useNavigate();
  const { subscriptionStatus } = useSubscriptionStatus();
  const { user } = useAuth();
  const isTankstelle = user?.industry === 'tankstelle';

  useEffect(() => {
    initializePage();
  }, []);

  const initializePage = async () => {
    // Fetch all data
    fetchData();
    fetchEmployeeLimits();
  };

  const fetchEmployeeLimits = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/employee-limits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployeeLimits(response.data);
    } catch (error) {
      console.error('Error fetching employee limits:', error);
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch locations + employees parallel (employees für Live-Zählung)
      let rawLocations = [];
      let employeeCountByLocation = {};
      try {
        const [locationsResponse, empResponse] = await Promise.all([
          axios.get(`${API}/api/admin/locations`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/api/admin/employees`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        ]);
        rawLocations = locationsResponse.data;

        // Zähle aktive (nicht-archivierte) Mitarbeiter pro Standort
        const employees = empResponse.data || [];
        employees.forEach(emp => {
          if (emp.is_archived || emp.status === 'inactive') return;
          (emp.location_ids || []).forEach(locId => {
            employeeCountByLocation[locId] = (employeeCountByLocation[locId] || 0) + 1;
          });
        });

        // Enrich each location with active_modules_count + live employee_count
        const enrichedLocations = await Promise.all(
          rawLocations.map(async (loc) => {
            try {
              const modRes = await axios.get(`${API}/api/admin/locations/${loc.id}/modules`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              const activeCount = (modRes.data.modules || []).filter(m => m.is_enabled).length;
              return {
                ...loc,
                active_modules_count: activeCount,
                employee_count: employeeCountByLocation[loc.id] || 0,  // Live-Zählung
              };
            } catch {
              return {
                ...loc,
                active_modules_count: 0,
                employee_count: employeeCountByLocation[loc.id] || 0,
              };
            }
          })
        );

        setLocations(enrichedLocations);
        setNeedsPayment(false);
      } catch (locError) {
        // Check if it's a 402 Payment Required error
        if (locError.response?.status === 402) {
          console.log('Payment required - showing paywall');
          setNeedsPayment(true);
          setLocations([]);
        } else {
          console.error('Error fetching locations:', locError);
          toast.error('Fehler beim Laden der Standorte');
          setLocations([]);
          setNeedsPayment(false);
        }
      }
      
      // Fetch subscription info (optional, don't fail if missing)
      try {
        const subResponse = await axios.get(`${API}/api/admin/subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubscription(subResponse.data);
      } catch (subError) {
        console.error('Error fetching subscription:', subError);
        setSubscription(null);
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error('Ein unerwarteter Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (location) => {
    setSelectedLocation(location);
    setEditFormData({
      name: location.name || '',
      address: location.street || location.address || '',
      station_number: location.station_number || '',
      brand: location.brand || '',
      task_daily_summary_time: location.task_daily_summary_time || '23:45',
      legal_betriebsnummer: location.legal_betriebsnummer || '',
      legal_unterzeichner: location.legal_unterzeichner || '',
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/admin/locations/${selectedLocation.id}`,
        editFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Standort aktualisiert');
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error updating location:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleDeleteClick = async (location) => {
    setSelectedLocation(location);
    
    // Count employees at this location
    try {
      const token = localStorage.getItem('token');
      
      // Get subscription to determine deletion date
      const subResponse = await axios.get(`${API}/api/payments/subscription-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const deletion_date = subResponse.data.subscription?.current_period_end || null;
      
      const empResponse = await axios.get(`${API}/api/admin/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const employeesAtLocation = empResponse.data.filter(emp => 
        emp.location_ids && emp.location_ids.includes(location.id)
      ).length;
      
      const currentLocationCost = locations.length === 1 ? 19.99 : 19.99 + (locations.length - 1) * 11.99;
      const newLocationCost = locations.length === 2 ? 19.99 : 19.99 + (locations.length - 2) * 11.99;
      
      setCostPreview({
        employeesAtLocation,
        currentLocations: locations.length,
        newLocations: locations.length - 1,
        currentEmployees: employeeLimits.total_employees,
        newEmployees: employeeLimits.total_employees - employeesAtLocation,
        currentLocationCost,
        newLocationCost,
        currentEmployeeCost: employeeLimits.cost_per_month,
        newEmployeeCost: Math.max(0, (employeeLimits.total_employees - employeesAtLocation - (locations.length - 1) * 10) * 0.99),
        deletion_date
      });
    } catch (error) {
      console.error('Error calculating preview:', error);
    }
    
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/api/admin/locations/${selectedLocation.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Check if deletion was scheduled (new behavior) or immediate (old behavior)
      if (response.data.scheduled_for_deletion) {
        const deletionDate = new Date(response.data.deletion_date).toLocaleDateString('de-DE', { 
          day: '2-digit', 
          month: '2-digit', 
          year: 'numeric' 
        });
        toast.success(`Standort "${selectedLocation.name}" wird am ${deletionDate} deaktiviert. Sie haben bis dahin vollen Zugriff.`);
      } else {
        toast.success(`Standort "${selectedLocation.name}" gelöscht`);
      }
      
      setShowDeleteDialog(false);
      fetchData();
      fetchEmployeeLimits();
    } catch (error) {
      console.error('Error deleting location:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleCancelDeletion = async (location) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/admin/locations/${location.id}/cancel-deletion`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Löschung von "${location.name}" abgebrochen. Standort bleibt aktiv.`);
      fetchData();
    } catch (error) {
      console.error('Error cancelling deletion:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Abbrechen der Löschung');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
        
        {/* Lieferschein-KI-Manager Add-on */}
        <LieferungAddonCard />

        {/* Header */}
        <div className="space-y-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <MapPin className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">Standorte</h1>
                <p className="text-sm sm:text-base text-muted-foreground">
                  Module pro Standort verwalten
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              {!subscriptionStatus?.locked && (
                <>
                  <Button 
                    onClick={() => navigate('/admin/locations/new')}
                    className="w-full sm:w-auto"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Standort hinzufügen
                  </Button>
                  
                  <Button 
                    onClick={() => navigate('/admin/locations/bulk-new')}
                variant="outline"
                className="w-full sm:w-auto"
              >
                <Plus className="h-4 w-4 mr-2" />
                Mehrere hinzufügen
              </Button>
                </>
              )}
              {subscriptionStatus?.locked && (
                <Button 
                  onClick={() => navigate('/admin/subscription')}
                  className="w-full sm:w-auto bg-orange-600 hover:bg-orange-700"
                >
                  Jetzt abonnieren
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Package Info Banner */}
        {subscription && subscription.package && (
          <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/20">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start gap-4">
                {/* Icon and Package Name side by side */}
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                    <Package className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                      {subscription.package.name}
                    </h3>
                  </div>
                </div>
                
                {/* Price Badge */}
                <div className="ml-auto flex-shrink-0">
                  <Badge className="bg-blue-600 hover:bg-blue-700 text-white">
                    {(subscription.package.price_monthly && typeof subscription.package.price_monthly === 'number') ? subscription.package.price_monthly.toFixed(2) : '0.00'}€/Monat
                  </Badge>
                </div>
              </div>
              
              {/* Description and Modules */}
              <div className="mt-4 space-y-3">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  {subscription.package.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {subscription.package.included_modules.map((moduleId) => (
                    <Badge 
                      key={moduleId}
                      variant="outline" 
                      className="text-xs border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-200"
                    >
                      {getModuleName(moduleId)}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Locations Grid */}
        {locations.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              {needsPayment ? (
                // Paywall state: User has locations but needs to subscribe
                <>
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900 mb-4">
                    <MapPin className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Ihr Standort ist bereit! 🎉</h3>
                  <p className="text-muted-foreground mb-2">
                    Ihr erster Standort wurde erfolgreich erstellt.
                  </p>
                  <p className="text-muted-foreground mb-6">
                    Schließen Sie jetzt Ihr Abonnement ab, um vollen Zugriff zu erhalten.
                  </p>
                  <div className="bg-blue-50 dark:bg-blue-950/30 p-4 rounded-lg mb-6 max-w-md mx-auto">
                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mb-1">
                      Starter: 29,90 € Netto / 35,58 € Brutto pro Monat
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      15 Mitarbeiter inklusive · Monatlich kündbar
                    </p>
                  </div>
                  <Button 
                    size="lg" 
                    className="px-8"
                    onClick={() => navigate('/admin/subscription')}
                  >
                    Abonnement jetzt starten →
                  </Button>
                </>
              ) : (
                // Normal empty state: User has no locations at all
                <>
                  <MapPin className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Keine Standorte vorhanden</h3>
                  <p className="text-muted-foreground mb-6">
                    Fügen Sie Ihren ersten Standort hinzu, um Module zu verwalten.
                  </p>
                  <GuardedButton requireWrite={true} onClick={() => navigate('/admin/locations/new')}>
                    <Plus className="h-4 w-4 mr-2" />
                    Ersten Standort anlegen
                  </GuardedButton>
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {locations.map((location) => {
              const isPending = location.billing_status === 'pending_payment';
              const isActive = location.billing_status === 'active' || location.billing_status === 'trialing';
              const isScheduledForDeletion = location.scheduled_for_deletion;
              
              return (
                <Card 
                  key={location.id}
                  className={`hover:shadow-lg transition-all group ${
                    isPending ? 'border-orange-300 dark:border-orange-700' : ''
                  } ${
                    isScheduledForDeletion ? 'border-red-300 dark:border-red-700 opacity-75' : ''
                  }`}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`p-2 rounded-lg transition-colors ${
                          isScheduledForDeletion
                            ? 'bg-red-100 dark:bg-red-900'
                            : isPending 
                            ? 'bg-orange-100 dark:bg-orange-900' 
                            : 'bg-primary/10 group-hover:bg-primary/20'
                        }`}>
                          <MapPin className={`h-5 w-5 ${
                            isScheduledForDeletion ? 'text-red-600 dark:text-red-400' :
                            isPending ? 'text-orange-600 dark:text-orange-400' : 'text-primary'
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                        <CardTitle className="text-lg leading-tight line-clamp-2">
                          {location.name}
                        </CardTitle>
                        {location.street && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {location.street} {location.house_number}
                            {location.postal_code && `, ${location.postal_code}`}
                          </p>
                        )}
                        {/* 🔥 SCHEDULED FOR DELETION BADGE */}
                        {isScheduledForDeletion && (
                          <Badge className="mt-2 bg-red-500 hover:bg-red-600 text-white text-xs">
                            🗑️ Wird deaktiviert am {new Date(location.scheduled_deletion_date).toLocaleDateString('de-DE')}
                          </Badge>
                        )}
                        {/* 🔥 PAYWALL BADGE */}
                        {!isScheduledForDeletion && isPending && (
                          <Badge className="mt-2 bg-orange-500 hover:bg-orange-600 text-white text-xs">
                            ⏳ Zahlung ausstehend
                          </Badge>
                        )}
                        {!isScheduledForDeletion && isActive && location.location_position === 1 && (
                          <Badge className="mt-2 bg-green-500 hover:bg-green-600 text-white text-xs">
                            ✓ Hauptstandort
                          </Badge>
                        )}
                        {location.brand && (
                          <Badge variant="outline" className="mt-2 text-xs border-blue-300 text-blue-700 dark:text-blue-300">
                            {location.brand}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-3">
                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-lg font-bold text-foreground">
                          {location.employee_count || 0}
                        </p>
                        <p className="text-xs text-muted-foreground">Mitarbeiter</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-lg font-bold text-foreground">
                          {location.active_modules_count || 0}
                        </p>
                        <p className="text-xs text-muted-foreground">Module aktiv</p>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  {isPending ? (
                    <div className="space-y-2">
                      <div className="p-3 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-700 rounded-lg">
                        <p className="text-xs text-orange-900 dark:text-orange-100">
                          💶 Bezahlen Sie {(location.total_price_gross && typeof location.total_price_gross === 'number') ? location.total_price_gross.toFixed(2) : '17.84'}€/Monat 
                          um diesen Standort zu nutzen
                        </p>
                      </div>
                      <Button 
                        className="w-full bg-orange-500 hover:bg-orange-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/locations/${location.id}/payment`);
                        }}
                      >
                        Jetzt bezahlen
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {isScheduledForDeletion ? (
                        /* 🗑️ SCHEDULED FOR DELETION - Show Cancel Button */
                        <div className="space-y-2">
                          <div className="p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-700 rounded-lg">
                            <p className="text-xs text-red-900 dark:text-red-100 font-semibold mb-1">
                              Standort zur Löschung vorgemerkt
                            </p>
                            <p className="text-xs text-red-800 dark:text-red-200">
                              Wird deaktiviert am: <strong>{new Date(location.scheduled_deletion_date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</strong>
                            </p>
                            <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                              Sie haben bis dahin vollen Zugriff auf den Standort.
                            </p>
                          </div>
                          <Button 
                            variant="outline"
                            className="w-full border-green-500 text-green-700 hover:bg-green-50 dark:hover:bg-green-950"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelDeletion(location);
                            }}
                          >
                            Löschung abbrechen
                          </Button>
                        </div>
                      ) : (
                        /* NORMAL ACTIVE - Show Regular Buttons */
                        <>
                          {/* Module verwalten Button */}
                          <Button 
                            variant="outline" 
                            className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/locations/${location.id}/modules`);
                            }}
                          >
                            <Settings className="h-4 w-4 mr-2" />
                            Module verwalten
                          </Button>
                          
                          {/* 🆕 Action Buttons Row - Mobile optimiert + GUARDED */}
                          <div className="flex gap-2">
                            <GuardedButton 
                              requireWrite={true}
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(location);
                              }}
                            >
                              <Edit2 className="h-4 w-4 mr-1" />
                              <span className="hidden sm:inline">Bearbeiten</span>
                            </GuardedButton>
                            <GuardedButton 
                              requireWrite={true}
                              variant="outline"
                              size="sm"
                              className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(location);
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              <span className="hidden sm:inline">Löschen</span>
                            </GuardedButton>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
              );
            })}
          </div>
        )}

        {/* 🆕 EDIT DIALOG */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-md max-h-[90dvh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Standort bearbeiten</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pb-4">
              <div>
                <Label htmlFor="edit_name">Standortname</Label>
                <Input
                  id="edit_name"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  placeholder="z.B. AVIA Mühlhausen"
                  enterKeyHint="next"
                />
              </div>
              <div>
                <Label htmlFor="edit_address">Adresse</Label>
                <Input
                  id="edit_address"
                  value={editFormData.address}
                  onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                  placeholder="z.B. Hauptstraße 123"
                  enterKeyHint="next"
                />
              </div>
              <div>
                <Label htmlFor="edit_station">Stationsnummer (optional)</Label>
                <Input
                  id="edit_station"
                  value={editFormData.station_number}
                  onChange={(e) => setEditFormData({ ...editFormData, station_number: e.target.value })}
                  placeholder="z.B. 12345"
                  enterKeyHint="done"
                />
              </div>
              {isTankstelle && (
                <div>
                  <Label htmlFor="edit_brand">Gesellschafter / Marke</Label>
                  <Select
                    value={editFormData.brand || ''}
                    onValueChange={(v) => setEditFormData({ ...editFormData, brand: v })}
                  >
                    <SelectTrigger id="edit_brand">
                      <SelectValue placeholder="Marke auswählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {BRAND_OPTIONS.map(b => (
                        <SelectItem key={b} value={b}>{b}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {/* Minijob / RV-Befreiungsantrag-Felder */}
              <div className="border-t pt-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Minijob-Zentrale / RV-Befreiungsantrag</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="edit_betriebsnr" className="text-xs">Betriebsnummer (8-stellig)</Label>
                    <Input
                      id="edit_betriebsnr"
                      value={editFormData.legal_betriebsnummer || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, legal_betriebsnummer: e.target.value })}
                      placeholder="z.B. 12345678"
                      maxLength={8}
                      className="h-8 text-sm font-mono"
                      data-testid="edit-betriebsnummer-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_unterzeichner" className="text-xs">Unterzeichner (AG-Vertreter)</Label>
                    <Input
                      id="edit_unterzeichner"
                      value={editFormData.legal_unterzeichner || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, legal_unterzeichner: e.target.value })}
                      placeholder="z.B. Max Mustermann"
                      className="h-8 text-sm"
                      data-testid="edit-unterzeichner-input"
                    />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2 sticky bottom-0 bg-background pt-2 border-t">
              <div className="w-full mb-2 border-t pt-3">
                <label className="text-sm font-medium text-foreground block mb-1">
                  Tagesabschluss-Push Uhrzeit
                  <span className="text-xs text-muted-foreground font-normal ml-2">(Zusammenfassung Aufgaben täglich um diese Uhrzeit)</span>
                </label>
                <input
                  type="time"
                  value={editFormData.task_daily_summary_time || '23:45'}
                  onChange={e => setEditFormData({ ...editFormData, task_daily_summary_time: e.target.value })}
                  className="h-9 w-36 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <Button variant="outline" onClick={() => setShowEditDialog(false)} className="w-full sm:w-auto">
                Abbrechen
              </Button>
              <Button onClick={handleSaveEdit} className="w-full sm:w-auto">
                Speichern
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 🆕 DELETE CONFIRMATION DIALOG */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="max-w-md sm:max-w-lg lg:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg lg:text-xl text-red-600 dark:text-red-400">
                🗑️ Standort löschen
              </DialogTitle>
            </DialogHeader>
            
            {costPreview && selectedLocation && (
              <div className="space-y-4 pb-4">
                <div className="bg-red-50 dark:bg-red-950/20 border-2 border-red-200 dark:border-red-800 rounded-lg p-4 lg:p-5">
                  <p className="font-semibold text-red-900 dark:text-red-100 mb-2 text-base lg:text-lg">⚠️ WARNUNG</p>
                  <div className="text-sm lg:text-base text-red-800 dark:text-red-200 space-y-1">
                    <p>• Standort <strong>{selectedLocation.name}</strong> wird dauerhaft gelöscht</p>
                    <p>• <strong>{costPreview.employeesAtLocation} Mitarbeiter</strong> werden entfernt</p>
                    <p>• Alle Daten (Messungen, Module) werden gelöscht</p>
                  </div>
                </div>

                {/* Cost comparison in 2 columns on large screens */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Current costs */}
                  <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 lg:p-5">
                    <p className="font-semibold text-blue-900 dark:text-blue-100 mb-3 text-base lg:text-lg">AKTUELL:</p>
                    <div className="space-y-2 text-sm lg:text-base">
                      <div className="flex justify-between">
                        <span>Standorte:</span>
                        <span className="font-semibold">{costPreview.currentLocations}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Mitarbeiter:</span>
                        <span className="font-semibold">{costPreview.currentEmployees}</span>
                      </div>
                      <div className="flex justify-between font-bold border-t pt-2 mt-2 text-base lg:text-lg">
                        <span>Kosten:</span>
                        <span>{(costPreview.currentLocationCost + costPreview.currentEmployeeCost).toFixed(2)}€/Monat</span>
                      </div>
                    </div>
                  </div>

                  {/* New costs after deletion */}
                  <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-4 lg:p-5">
                    <p className="font-semibold text-green-900 dark:text-green-100 mb-3 text-base lg:text-lg">NACH LÖSCHUNG:</p>
                    <div className="space-y-2 text-sm lg:text-base">
                      <div className="flex justify-between">
                        <span>Standorte:</span>
                        <span className="font-semibold">{costPreview.newLocations} (-1)</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Mitarbeiter:</span>
                        <span className="font-semibold">{costPreview.newEmployees} (-{costPreview.employeesAtLocation})</span>
                      </div>
                      <div className="flex justify-between font-bold border-t pt-2 mt-2 text-base lg:text-lg">
                        <span>Neue Kosten:</span>
                        <span className="text-green-700">{(costPreview.newLocationCost + costPreview.newEmployeeCost).toFixed(2)}€/Monat</span>
                      </div>
                      <div className="flex justify-between text-xs lg:text-sm text-green-600 mt-1">
                        <span>Ersparnis:</span>
                        <span className="font-semibold">-{((costPreview.currentLocationCost + costPreview.currentEmployeeCost) - (costPreview.newLocationCost + costPreview.newEmployeeCost)).toFixed(2)}€/Monat</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 lg:p-4">
                  <p className="text-xs lg:text-sm text-amber-800 dark:text-amber-200">
                    <strong>💰 Abrechnung:</strong> Der Standort wird zur Löschung vorgemerkt und läuft bis zum Ende Ihrer aktuellen Abrechnungsperiode weiter. Sie behalten vollen Zugriff bis zum: <strong>{costPreview.deletion_date ? new Date(costPreview.deletion_date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Ende der Periode'}</strong>. Danach wird der Standort automatisch deaktiviert.
                  </p>
                </div>
              </div>
            )}

            <DialogFooter className="flex-col sm:flex-row gap-2 lg:gap-3 mt-4 sticky bottom-0 bg-card pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => setShowDeleteDialog(false)}
                className="w-full sm:w-auto lg:min-w-[120px] lg:text-base"
              >
                Abbrechen
              </Button>
              <Button 
                onClick={confirmDelete}
                variant="destructive"
                className="w-full sm:w-auto lg:min-w-[180px] lg:text-base"
              >
                Standort löschen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </DashboardLayout>
  );
};

export default AdminLocations;
